import { memo, useRef } from "react";
import { Pressable, View } from "react-native";

import { tailwind } from "src/foundations/styles";
import { Icon, Paragraph } from "src/foundations/ui";
import useKeyDown from "src/ui/hooks/useKeydown";

type TreeNodeViewProps = {
  nativeID: string;
  depth: number;
  content: string;
  handleKeydown: (e: KeyboardEvent) => void;
  /**
   * Element ID of any subtree this item might own in the case of a parent node.
   */
  childrenContainerId?: string;
  isOpen?: boolean;
  isSelected?: boolean;
  isFocusable?: boolean;
  hasChildren?: boolean;
  onNodePress?: () => void;
  onTogglePress?: () => void;
};

const TreeNodeView: React.FC<TreeNodeViewProps> = (props) => {
  const pressableRef = useRef<View | null>(null);

  useKeyDown(pressableRef, props.handleKeydown, [props.handleKeydown]);

  return (
    // If we need view customization, make sure to extract only the view parts to maintain the a11y
    // properties intact.
    <Pressable
      nativeID={`treeitem-${props.nativeID}`}
      ref={pressableRef}
      style={[
        tailwind(`flex flex-row items-center p-2 pl-${2 + props.depth * 5} rounded-lg`),
        props.isSelected && tailwind("bg-blue-lighter"),
      ]}
      onPress={props.onNodePress}
      // @ts-expect-error TS2769: No overload matches this call.
      accessibilityRole="treeitem"
      accessibilityLabel={props.content}
      focusable={props.isFocusable ?? false}
      accessibilityOwns={props.childrenContainerId}
      accessibilityState={{ expanded: props.isOpen, selected: props.isSelected }}
    >
      {props.hasChildren ? (
        <View style={[tailwind("w-5 h-5 flex items-center justify-center")]}>
          <Pressable
            onPress={props.onTogglePress}
            focusable={false}
            accessibilityRole="button"
            accessibilityLabel={`Toggle ${props.content}`}
          >
            <Icon name={props.isOpen ? "down-triangle-small" : "right-triangle-small"} size={20} />
          </Pressable>
        </View>
      ) : null}
      {/* Margin left is arbitrary because we want it to align with the sibling's close/open icon */}
      <View
        style={[tailwind("flex flex-1 justify-center"), { marginLeft: props.hasChildren ? 4 : 6 }]}
      >
        <Paragraph level={2} numberOfLines={1}>
          {props.content}
        </Paragraph>
      </View>
    </Pressable>
  );
};

/**
 * Responsibilities:
 * - Determine the looks for a tree item
 * - Assign a11y properties for tree item
 * - Assign properties used for focus management (keyboard nav)
 */
export default memo(TreeNodeView) as typeof TreeNodeView;
